import React, { useState, useEffect } from 'react';
import { FiShield, FiPlus, FiCalendar, FiMapPin, FiTrash2, FiActivity, FiAward, FiEdit2 } from 'react-icons/fi';
import { dataFetch } from '../api';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import Textarea from '../components/ui/Textarea';
import { formatDate, getTodayDateString } from '../lib/utils';

const Tournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', date: getTodayDateString(), location: '' });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [resultsFormData, setResultsFormData] = useState({
    weight_class: '',
    placement: '',
    matches_won: 0,
    matches_lost: 0,
    submissions: 0,
    notes: ''
  });

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    setLoading(true);
    const userId = localStorage.getItem('user_id');
    const res = await dataFetch(`read/tournaments?owner_id=${userId}`);
    if (res?.status === 'success') {
      const sorted = (res?.data ?? []).sort((a, b) => new Date(b.date) - new Date(a.date));
      setTournaments(sorted);
    } else {
      setError(res?.error || 'Failed to load tournaments');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const userId = localStorage.getItem('user_id');
    
    if (!formData.name || !formData.date || !formData.location) {
      setError('Please fill out all fields');
      setSubmitting(false);
      return;
    }

    const payload = {
      ...formData,
      user_id: userId
    };

    const res = await dataFetch(`create/tournaments?owner_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    if (res?.status === 'success') {
      const newTournament = res.data;
      const newList = [newTournament, ...tournaments].sort((a, b) => new Date(b.date) - new Date(a.date));
      setTournaments(newList);
      setFormData({ name: '', date: getTodayDateString(), location: '' });
      setShowAddForm(false);
    } else {
      setError(res?.error || 'Failed to add tournament');
    }
    setSubmitting(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the tournament "${name}"?`)) return;
    
    const userId = localStorage.getItem('user_id');
    const res = await dataFetch(`delete/tournaments/${id}?owner_id=${userId}`, {
      method: 'DELETE'
    });

    if (res?.status === 'success') {
      setTournaments(tournaments.filter(t => t.id !== id));
    } else {
      alert(res?.error || 'Failed to delete tournament');
    }
  };

  const openResultsForm = (tournament) => {
    setEditingId(tournament.id);
    setResultsFormData({
      weight_class: tournament.weight_class || '',
      placement: tournament.placement || '',
      matches_won: tournament.matches_won || 0,
      matches_lost: tournament.matches_lost || 0,
      submissions: tournament.submissions || 0,
      notes: tournament.notes || ''
    });
  };

  const handleSaveResults = async (tournament) => {
    const userId = localStorage.getItem('user_id');
    
    const payload = {
      date: tournament.date,
      location: tournament.location,
      name: tournament.name,
      weight_class: resultsFormData.weight_class,
      placement: resultsFormData.placement,
      matches_won: parseInt(resultsFormData.matches_won, 10) || 0,
      matches_lost: parseInt(resultsFormData.matches_lost, 10) || 0,
      submissions: parseInt(resultsFormData.submissions, 10) || 0,
      notes: resultsFormData.notes,
      user_id: userId
    };
    
    const res = await dataFetch(`update/tournaments/${tournament.id}?owner_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });

    if (res?.status === 'success') {
      setTournaments(tournaments.map(t => t.id === tournament.id ? { ...t, ...payload } : t));
      setEditingId(null);
    } else {
      alert(res?.error || 'Failed to save results');
    }
  };

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-200px)] items-center justify-center">
        <div className="text-slate-500 dark:text-slate-400 flex flex-col items-center">
          <FiShield className="w-12 h-12 mb-4 animate-pulse opacity-50" />
          <p>Loading tournaments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2 flex items-center">
            <FiShield className="mr-3 text-yellow-500 dark:text-yellow-400" />
            Tournaments
          </h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">Track your competitive matches and events.</p>
        </div>
        {!showAddForm && (
          <Button onClick={() => setShowAddForm(true)} className="w-full sm:w-auto">
            <FiPlus className="w-4 h-4 mr-2" />
            Add Tournament
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
          {error}
        </div>
      )}

      {showAddForm && (
        <Card className="mb-8 border-blue-200 dark:border-blue-800 dark:bg-slate-800/50 shadow-md">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Log New Tournament</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Tournament Name</Label>
                  <Input 
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g. IBJJF Open, NAGA"
                    required
                  />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input 
                    type="date"
                    value={formData.date}
                    onChange={e => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Location</Label>
                  <Input 
                    value={formData.location}
                    onChange={e => setFormData({...formData, location: e.target.value})}
                    placeholder="City, State or Venue"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowAddForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Tournament'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tournaments.map((tournament) => (
          <Card key={tournament.id} className="hover:shadow-md transition-shadow hover:border-blue-300 dark:hover:border-blue-700 dark:border-slate-800">
            <CardContent className="p-6 relative group">
              <button
                onClick={() => handleDelete(tournament.id, tournament.name)}
                className="absolute top-4 right-4 p-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                title="Delete Tournament"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
              
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center text-yellow-600 shrink-0 dark:bg-yellow-900/30 dark:text-yellow-400">
                  <FiShield className="w-6 h-6" />
                </div>
                <div className="min-w-0 pr-6">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white truncate" title={tournament.name}>
                    {tournament.name}
                  </h3>
                  <div className="flex items-center text-sm text-slate-500 dark:text-slate-400 mt-1">
                    <FiCalendar className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                    {formatDate(tournament.date)}
                  </div>
                  <div className="flex items-center text-sm text-slate-500 dark:text-slate-400 mt-1">
                    <FiMapPin className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                    <span className="truncate" title={tournament.location}>{tournament.location}</span>
                  </div>
                </div>
              </div>

              {editingId === tournament.id ? (
                <div className="space-y-4 mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 -mx-2 px-2 rounded-lg pb-2">
                  <h4 className="font-semibold text-slate-800 dark:text-slate-200 text-sm">Tournament Results</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Weight Class</Label>
                      <Input 
                        value={resultsFormData.weight_class}
                        onChange={e => setResultsFormData({...resultsFormData, weight_class: e.target.value})}
                        placeholder="e.g. 155 lbs"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Placement</Label>
                      <Input 
                        value={resultsFormData.placement}
                        onChange={e => setResultsFormData({...resultsFormData, placement: e.target.value})}
                        placeholder="e.g. 1st, Gold"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Wins</Label>
                      <Input 
                        type="number"
                        min="0"
                        value={resultsFormData.matches_won}
                        onChange={e => setResultsFormData({...resultsFormData, matches_won: e.target.value})}
                        className="h-8 text-sm text-center"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Losses</Label>
                      <Input 
                        type="number"
                        min="0"
                        value={resultsFormData.matches_lost}
                        onChange={e => setResultsFormData({...resultsFormData, matches_lost: e.target.value})}
                        className="h-8 text-sm text-center"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Subs</Label>
                      <Input 
                        type="number"
                        min="0"
                        value={resultsFormData.submissions}
                        onChange={e => setResultsFormData({...resultsFormData, submissions: e.target.value})}
                        className="h-8 text-sm text-center"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Notes</Label>
                    <Textarea 
                      value={resultsFormData.notes}
                      onChange={e => setResultsFormData({...resultsFormData, notes: e.target.value})}
                      placeholder="What went well? What needs work?"
                      className="min-h-[60px] text-sm py-1.5"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                    <Button size="sm" onClick={() => handleSaveResults(tournament)}>Save</Button>
                  </div>
                </div>
              ) : (
                <>
                  {tournament.weight_class || tournament.placement || tournament.matches_won > 0 || tournament.matches_lost > 0 ? (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center">
                          <FiAward className="w-4 h-4 mr-1.5 text-yellow-500 dark:text-yellow-400" />
                          Results
                        </h4>
                        <button 
                          onClick={() => openResultsForm(tournament)}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
                        >
                          <FiEdit2 className="w-3 h-3 mr-1" />
                          Edit
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        {tournament.weight_class && (
                          <div className="truncate" title={tournament.weight_class}>
                            <span className="text-slate-500 dark:text-slate-400 text-xs">Weight</span><br/>
                            <span className="font-medium text-slate-700 dark:text-slate-300">{tournament.weight_class}</span>
                          </div>
                        )}
                        {tournament.placement && (
                          <div className="truncate" title={tournament.placement}>
                            <span className="text-slate-500 dark:text-slate-400 text-xs">Placement</span><br/>
                            <span className="font-medium text-slate-700 dark:text-slate-300">{tournament.placement}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm bg-slate-50 dark:bg-slate-800/50 p-2 rounded-md mb-3 border border-slate-100 dark:border-slate-700">
                        <div className="flex flex-col items-center flex-1">
                          <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">Record</span>
                          <span className="font-bold text-slate-800 dark:text-slate-200">{tournament.matches_won || 0}W - {tournament.matches_lost || 0}L</span>
                        </div>
                        <div className="h-6 w-px bg-slate-300 dark:bg-slate-600"></div>
                        <div className="flex flex-col items-center flex-1">
                          <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">Subs</span>
                          <span className="font-bold text-slate-800 dark:text-slate-200">{tournament.submissions || 0}</span>
                        </div>
                      </div>
                      
                      {tournament.notes && (
                        <div className="text-sm text-slate-600 dark:text-slate-400 italic bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-700 p-2 rounded-md line-clamp-2" title={tournament.notes}>
                          "{tournament.notes}"
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/30"
                        onClick={() => openResultsForm(tournament)}
                      >
                        <FiPlus className="w-4 h-4 mr-2" />
                        Add Results
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {tournaments.length === 0 && !showAddForm && !loading && (
        <div className="text-center py-16 bg-slate-50 dark:bg-slate-800/30 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700">
          <FiActivity className="w-12 h-12 mx-auto mb-4 text-slate-400 dark:text-slate-500" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No Tournaments Logged</h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6">Start tracking your competition history by logging your first tournament.</p>
          <Button onClick={() => setShowAddForm(true)}>
            <FiPlus className="w-4 h-4 mr-2" />
            Add Tournament
          </Button>
        </div>
      )}
    </div>
  );
};

export default Tournaments;